import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Gift,
  Headphones,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

import { Button, Logo } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { submitContactInquiry, type ContactInquiryPayload } from "@/lib/endpoints";

export function ContactScreen() {
  const navigate = useNavigate();

  // Lee el parámetro ?prueba=true de la URL para preseleccionar la prueba gratis
  const isTrialInitial = (() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("prueba") !== "false";
    }
    return true;
  })();

  const [fullName, setFullName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessType, setBusinessType] = useState<string>("pos");
  const [isFreeTrialRequested, setIsFreeTrialRequested] = useState(isTrialInitial);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: ContactInquiryPayload) => submitContactInquiry(payload),
    onSuccess: () => {
      setIsSuccess(true);
      setErrorMessage(null);
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : "No se pudo enviar tu solicitud. Por favor intenta de nuevo o escríbenos directamente a WhatsApp.",
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    mutation.mutate({
      full_name: fullName,
      business_name: businessName,
      email,
      phone,
      business_type: businessType,
      is_free_trial_requested: isFreeTrialRequested,
      message,
    });
  };

  const waDirectUrl = `https://wa.me/528672131335?text=Hola,%20quisiera%20informaci%C3%B3n%20sobre%20el%20sistema%20MegaSyR${
    businessName ? `%20para%20mi%20negocio%20${encodeURIComponent(businessName)}` : ""
  }`;

  return (
    <div className="min-h-screen bg-[#0c0f12] text-white flex flex-col selection:bg-electric selection:text-white">
      {/* ------------------------------------------------------------------------- */}
      {/* Encabezado Superior */}
      {/* ------------------------------------------------------------------------- */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0c0f12]/90 backdrop-blur-md px-4 sm:px-6 py-3.5 sm:py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3">
            <Logo variant="isotipo" tone="white" className="h-8 sm:h-9" />
            <Logo variant="wordmark" tone="white" className="h-4.5 sm:h-5" alt="MegaSyR" />
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="size-3.5 sm:size-4" />
              <span className="hidden xs:inline">Volver a Inicio</span>
              <span className="xs:hidden">Inicio</span>
            </Link>

            <Button
              variant="outline"
              size="sm"
              onClick={() => void navigate({ to: "/login" })}
              className="text-xs sm:text-sm px-2.5 sm:px-3"
            >
              Iniciar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------------------- */}
      {/* Cuerpo Principal */}
      {/* ------------------------------------------------------------------------- */}
      <main className="flex-1 py-8 sm:py-12 px-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          {/* Título de la sección */}
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-electric/40 bg-electric/10 px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold text-cyan-300 mb-3 sm:mb-4">
              <Sparkles className="size-3.5 text-cyan-400" />
              <span>Atención Directa · Asesoría Sin Compromiso</span>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Contáctanos y Comienza tu{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-cyan-400">
                Prueba Gratuita
              </span>
            </h1>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg text-white/70 leading-relaxed">
              ¿Tienes dudas sobre cómo implementar MegaSyR o quieres probar la plataforma en tu negocio?
              Déjanos tu consulta o escríbenos directamente por WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 items-start">
            {/* ------------------------------------------------------------------- */}
            {/* Columna Izquierda: Información de Contacto y Beneficios */}
            {/* ------------------------------------------------------------------- */}
            <div className="lg:col-span-5 space-y-6">
              {/* Tarjeta de Soporte y Ubicación */}
              <div className="rounded-2xl border border-white/10 bg-ink-soft p-5 sm:p-6 space-y-5 sm:space-y-6">
                <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <Headphones className="size-5 text-electric" />
                  <span>Canales de Atención Directa</span>
                </h2>

                <div className="space-y-3.5 sm:space-y-4">
                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/528672131335?text=Hola,%20me%20gustar%C3%ADa%20informaci%C3%B3n%20sobre%20MegaSyR"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 sm:gap-4 p-3 sm:p-3.5 rounded-xl bg-white/5 border border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/10 transition-all group"
                  >
                    <div className="p-2 sm:p-2.5 rounded-lg bg-emerald-500/20 text-emerald-400 group-hover:scale-110 transition-transform shrink-0">
                      <MessageSquare className="size-5" />
                    </div>
                    <div>
                      <p className="text-[11px] text-white/50 uppercase tracking-wider font-semibold">WhatsApp Oficial</p>
                      <p className="text-base font-bold text-white group-hover:text-emerald-400">867 213 1335</p>
                      <p className="text-xs text-white/60">Atención rápida y demostraciones en vivo</p>
                    </div>
                  </a>

                  {/* Teléfono directo */}
                  <a
                    href="tel:+528672131335"
                    className="flex items-start gap-3.5 sm:gap-4 p-3 sm:p-3.5 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 transition-all group"
                  >
                    <div className="p-2 sm:p-2.5 rounded-lg bg-cyan-500/20 text-cyan-400 group-hover:scale-110 transition-transform shrink-0">
                      <Phone className="size-5" />
                    </div>
                    <div>
                      <p className="text-[11px] text-white/50 uppercase tracking-wider font-semibold">Llamada Telefónica</p>
                      <p className="text-base font-bold text-white group-hover:text-cyan-400">(867) 213-1335</p>
                      <p className="text-xs text-white/60">Lunes a Sábado de 8:00 AM a 8:00 PM</p>
                    </div>
                  </a>

                  {/* Correo Electrónico */}
                  <a
                    href="mailto:contacto@megasyr.mx"
                    className="flex items-start gap-3.5 sm:gap-4 p-3 sm:p-3.5 rounded-xl bg-white/5 border border-white/10 hover:border-indigo-500/50 hover:bg-indigo-500/10 transition-all group"
                  >
                    <div className="p-2 sm:p-2.5 rounded-lg bg-indigo-500/20 text-indigo-400 group-hover:scale-110 transition-transform shrink-0">
                      <Mail className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-white/50 uppercase tracking-wider font-semibold">Correo Electrónico</p>
                      <p className="text-base font-bold text-white group-hover:text-indigo-400 break-all sm:break-normal">contacto@megasyr.mx</p>
                      <p className="text-xs text-white/60">Cotizaciones, facturación y cuentas corporativas</p>
                    </div>
                  </a>

                  {/* Ubicación Física y Cobertura */}
                  <div className="flex items-start gap-3.5 sm:gap-4 p-3 sm:p-3.5 rounded-xl bg-white/5 border border-white/10">
                    <div className="p-2 sm:p-2.5 rounded-lg bg-white/10 text-white/80 shrink-0">
                      <MapPin className="size-5" />
                    </div>
                    <div>
                      <p className="text-[11px] text-white/50 uppercase tracking-wider font-semibold">Soporte y Atención</p>
                      <p className="text-base font-bold text-white">Tlalixcoyan, Veracruz</p>
                      <p className="text-xs text-white/60">Atención presencial y soporte remoto a nivel nacional</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Beneficios de la prueba */}
              <div className="rounded-2xl border border-electric/30 bg-gradient-to-br from-electric/10 via-ink-soft to-ink-soft p-5 sm:p-6 space-y-4">
                <h3 className="text-sm sm:text-base font-bold text-cyan-300 flex items-center gap-2">
                  <Gift className="size-5 text-cyan-400 shrink-0" />
                  <span>¿Qué incluye tu prueba gratuita de 15 días?</span>
                </h3>
                <ul className="space-y-2.5 text-xs sm:text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>Acceso completo a tu vertical (Punto de Venta, Restaurante o Taller).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>Catálogo de productos, control de inventario y caja chica.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>Modo sin conexión garantizado en tu sucursal.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span>Sin necesidad de tarjeta de crédito ni compromisos forzosos.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* ------------------------------------------------------------------- */}
            {/* Columna Derecha: Formulario de Contacto / Prueba Gratis */}
            {/* ------------------------------------------------------------------- */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-white/10 bg-ink-soft p-5 sm:p-8 shadow-xl">
                {isSuccess ? (
                  <div className="text-center py-6 sm:py-8 space-y-6">
                    <div className="mx-auto size-14 sm:size-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
                      <CheckCircle2 className="size-8 sm:size-9" />
                    </div>

                    <div className="space-y-2">
                      <h2 className="text-xl sm:text-2xl font-bold text-white">¡Solicitud Recibida con Éxito!</h2>
                      <p className="text-white/70 max-w-md mx-auto text-xs sm:text-sm leading-relaxed">
                        Muchas gracias, <strong className="text-white">{fullName}</strong>. Hemos recibido tus datos y
                        nos pondremos en contacto contigo al correo <strong className="text-white">{email}</strong> y por
                        WhatsApp a la brevedad.
                      </p>
                    </div>

                    <div className="pt-2 sm:pt-4 flex flex-col sm:flex-row gap-3 justify-center">
                      <a
                        href={waDirectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-msr bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-900/30"
                      >
                        <MessageSquare className="size-4" />
                        <span>Chatear por WhatsApp Ahora</span>
                      </a>

                      <Button
                        variant="secondary"
                        size="md"
                        onClick={() => {
                          setIsSuccess(false);
                          setFullName("");
                          setBusinessName("");
                          setEmail("");
                          setPhone("");
                          setMessage("");
                        }}
                      >
                        Enviar otra consulta
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-white">Envíanos tu consulta</h2>
                      <p className="text-xs sm:text-sm text-white/60 mt-1">
                        Completa el formulario y te responderemos en minutos durante horario laboral.
                      </p>
                    </div>

                    {errorMessage ? (
                      <div className="rounded-msr bg-danger/15 border border-danger/30 p-3 text-sm text-danger flex items-start gap-2">
                        <span>{errorMessage}</span>
                      </div>
                    ) : null}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Nombre Completo */}
                      <div>
                        <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                          Tu Nombre Completo *
                        </label>
                        <input
                          type="text"
                          required
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="Ej. Juan Pérez"
                          className="h-12 w-full rounded-msr border border-white/15 bg-white/5 px-3.5 text-base text-white placeholder:text-white/30 focus:border-electric focus:outline-none focus:ring-1 focus:ring-electric"
                        />
                      </div>

                      {/* Nombre del Negocio */}
                      <div>
                        <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                          Nombre del Negocio / Empresa *
                        </label>
                        <input
                          type="text"
                          required
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Ej. Taller Mecánico Los Pinos"
                          className="h-12 w-full rounded-msr border border-white/15 bg-white/5 px-3.5 text-base text-white placeholder:text-white/30 focus:border-electric focus:outline-none focus:ring-1 focus:ring-electric"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Correo Electrónico */}
                      <div>
                        <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                          Correo Electrónico *
                        </label>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="ejemplo@negocio.mx"
                          className="h-12 w-full rounded-msr border border-white/15 bg-white/5 px-3.5 text-base text-white placeholder:text-white/30 focus:border-electric focus:outline-none focus:ring-1 focus:ring-electric"
                        />
                      </div>

                      {/* Teléfono / WhatsApp */}
                      <div>
                        <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                          Teléfono / WhatsApp *
                        </label>
                        <input
                          type="tel"
                          required
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Ej. 867 123 4567"
                          className="h-12 w-full rounded-msr border border-white/15 bg-white/5 px-3.5 text-base text-white placeholder:text-white/30 focus:border-electric focus:outline-none focus:ring-1 focus:ring-electric"
                        />
                      </div>
                    </div>

                    {/* Giro del negocio */}
                    <div>
                      <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                        Giro Principal o Solución de Interés
                      </label>
                      <select
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                        className="h-12 w-full rounded-msr border border-white/15 bg-[#13181d] px-3 text-sm sm:text-base text-white focus:border-electric focus:outline-none focus:ring-1 focus:ring-electric"
                      >
                        <option value="pos">Punto de Venta / Retail (Comercio, Tienda, Papelería)</option>
                        <option value="restaurant">Restaurante / Comandas (Mesas, Cocina, Barra)</option>
                        <option value="workshop">Taller Mecánico / Reparación (Órdenes de servicio, Refacciones)</option>
                        <option value="multiple">Múltiples Giros / Paquete Completo</option>
                      </select>
                    </div>

                    {/* Checkbox de Solicitud de Prueba Gratis */}
                    <div className="rounded-xl border border-electric/30 bg-electric/10 p-3.5 sm:p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isFreeTrialRequested}
                          onChange={(e) => setIsFreeTrialRequested(e.target.checked)}
                          className="mt-1 size-4 rounded border-white/30 text-electric focus:ring-electric"
                        />
                        <div>
                          <p className="text-sm font-bold text-white flex items-center gap-1.5">
                            <Gift className="size-4 text-cyan-400 shrink-0" />
                            <span>Deseo solicitar mi cuenta de prueba gratuita por 15 días</span>
                          </p>
                          <p className="text-xs text-white/70 mt-0.5">
                            Te configuramos un entorno de pruebas con tus productos y turnos sin costo alguno.
                          </p>
                        </div>
                      </label>
                    </div>

                    {/* Mensaje / Consulta */}
                    <div>
                      <label className="block text-xs font-semibold text-white/70 uppercase tracking-wider mb-1.5">
                        Consulta o Detalles de tu Negocio
                      </label>
                      <textarea
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Escribe tus dudas, número de sucursales o lo que requieres para operar..."
                        className="w-full rounded-msr border border-white/15 bg-white/5 p-3 text-base text-white placeholder:text-white/30 focus:border-electric focus:outline-none focus:ring-1 focus:ring-electric resize-y"
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={mutation.isPending}
                      className="flex items-center justify-center gap-2 shadow-lg shadow-electric/25 py-3.5 text-base font-semibold"
                    >
                      <Send className="size-4" />
                      <span>{isFreeTrialRequested ? "Solicitar mi Prueba Gratuita" : "Enviar Consulta"}</span>
                    </Button>

                    <p className="text-center text-[11px] text-white/40">
                      Tus datos son privados y se usan exclusivamente para responder a tu solicitud.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ------------------------------------------------------------------------- */}
      {/* Pie de Página */}
      {/* ------------------------------------------------------------------------- */}
      <footer className="border-t border-white/10 bg-[#0c0f12] py-6 sm:py-8 px-4 sm:px-6 text-center text-xs text-white/50">
        <div className="mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <Logo variant="isotipo" tone="white" className="h-6" />
            <span>MegaSyR SaaS · Punto de Venta, Restaurantes y Talleres</span>
          </div>

          <p className="leading-relaxed">
            Atención y Soporte: Tlalixcoyan, Veracruz · WhatsApp: <strong>(867) 213-1335</strong> ·{" "}
            <a href="mailto:contacto@megasyr.mx" className="text-white/70 hover:text-white underline">
              contacto@megasyr.mx
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
